import { FunctionComponent } from "react";
import GalleryTemplate from "./GalleryTemplate";
import useCurrentTime from "../../utils/useCurrentTime";
import { format } from "date-fns";
import { de, enUS, fr, it } from "date-fns/locale";

export interface IGalleryFullscreen {
  configsFromLayout: any;
}
const GalleryFullscreen: FunctionComponent<IGalleryFullscreen> = ({
  configsFromLayout,
}) => {
  const duration = configsFromLayout.configs.duration.value * 1000;
  const getConfig = (key: string) => configsFromLayout?.configs?.[key]?.value;

  const fontFamilyStylePrimary = {
    fontFamily: `'${configsFromLayout?.configs?.primary_font?.value}', sans-serif`,
  };
  const fontFamilyStyleSecondary = {
    fontFamily: `'${configsFromLayout?.configs?.secondary_font?.value}', sans-serif`,
  };

  const language = getConfig("language") || "german";
  const localeMap: { [key: string]: string } = {
    german: "de-DE",
    english: "en-US",
    french: "fr-FR",
    italian: "it-IT",
  };
  const locale = localeMap[language.toLowerCase()] || "de-DE";

  const dateFnsLocaleMap: { [key: string]: Locale } = {
    "de-DE": de,
    "en-US": enUS,
    "fr-FR": fr,
    "it-IT": it,
  };
  const dateFnsLocale = dateFnsLocaleMap[locale] || de;

  const { currentDate: rawDate, currentTime } = useCurrentTime();

  const currentDate = format(rawDate, "EEEE, d. MMMM yyyy", {
    locale: dateFnsLocale,
  });

  function SingleMediaCases(post: any, index: number) {
    if (typeof post === "string") {
      return (
        <img
          src={post}
          alt={`Slide ${index}`}
          className={
            getConfig("stretch_image")
              ? "w-full h-full object-cover"
              : "max-w-[100%] max-h-[100%] object-contain"
          }
        />
      );
    }
  }
  return (
    <div
      className="w-full h-screen relative"
      style={{
        background: `${configsFromLayout?.configs?.background_color?.value}`,
      }}
    >
      <GalleryTemplate
        configs={{
          autoplay: { delay: duration },
          slidesPerView: 1,
          speed: 2500,
          className: "h-full w-full",
          transition: getConfig("transition"),
        }}
        showOverlay={false}
        shouldShowBackground={false}
        children={configsFromLayout?.configs?.gallery?.value.map(
          (post: any, index: number) => (
            <div
              key={index}
              className="landscape:flex justify-center w-full h-full"
            >
              <div className="landscape:w-[100%] landscape:flex justify-center portrait:w-full h-full">
                {SingleMediaCases(post, index)}
              </div>
            </div>
          ),
        )}
        configsFromLayout={configsFromLayout}
      />

      {getConfig("logo") && (
        <div className="absolute top-[5.5vh] landscape:left-[5.5vh] portrait:left-[2.5vh] z-10">
          <img
            alt={getConfig("logo")}
            src={getConfig("logo")}
            className="rounded-[9px] w-[calc(15vw_+_8vh)] object-contain"
          />
        </div>
      )}

      {getConfig("show_date") && (
        <div className="absolute text-center  top-[5.5vh] landscape:right-[5.5vh] portrait:right-[2.5vh] z-10 bg-white/50 rounded-[8px] p-[1.5vh] ">
          <p
            className=" text-[calc(1.8vw_+_1.8vh)] leading-none"
            style={{
              color: configsFromLayout?.configs?.text?.value || "#000",
              ...fontFamilyStylePrimary,
            }}
          >
            {currentTime}
          </p>
          <p
            className="font-medium text-[calc(0.9vw_+_0.9vh)] mt-[0.5vh]"
            style={{
              color: configsFromLayout?.configs?.text?.value || "#000",
              ...fontFamilyStyleSecondary,
            }}
          >
            {currentDate}
          </p>
        </div>
      )}

      <div className="absolute landscape:bottom-[7vh] portrait:bottom-[4vh] flex flex-col items-center space-y-3 left-1/2 transform -translate-x-1/2 z-10">
        {getConfig("title") && (
          <div className="bg-white/50 rounded-[8px] p-[2vh] text-center w-auto inline-block">
            <p
              className="text-[calc(2.7vw_+_2.7vh)]  tracking-wide whitespace-nowrap"
              style={{
                color: configsFromLayout?.configs?.text?.value || "#000",
                ...fontFamilyStylePrimary,
              }}
            >
              {getConfig("title")}
            </p>
          </div>
        )}

        {getConfig("description") && (
          <div className="bg-white/50 rounded-[8px] p-[1.5vh] text-center w-auto inline-block">
            <p
              className="font-medium text-[calc(1.5vw_+_1.5vh)] whitespace-nowrap"
              style={{
                color: configsFromLayout?.configs?.text?.value || "#000",
                ...fontFamilyStyleSecondary,
              }}
            >
              {getConfig("description")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryFullscreen;
